<template>
  <form id="booking-info" @submit.prevent="submit">
    <b-row>
      <b-col sm="12" md="6">
        <b-card :title="$t('bookings.route')">
          <fields group="route" />
          <div v-if="booking.created_at" class="form-group row">
            <label class="col-4 col-form-label text-right">
              {{ $t('bookings.created') }}
            </label>
            <div class="col-8 col-form-label">
              {{ booking.created_at | moment($dateFormat) }}
            </div>
          </div>
        </b-card>
        <div
          v-if="booking"
          ref="mapWrapper"
          class="mb-4 embed-responsive embed-responsive-16by9"
        >
          <div id="booking-map" ref="map" class="embed-responsive-item rounded">
            Map is loading ...
          </div>
        </div>
        <b-card ref="estimation" :title="$t('bookings.estimation')">
          <b-row v-if="vehicle" class="mb-4">
            <b-col cols="4" class="text-right">
              {{ $t('bookings.vehicle_class') }}
            </b-col>
            <b-col cols="8">
              <BookingVehicle
                :vehicle="vehicle"
                :sharing-vehicle="sharingVehicle"
              />
            </b-col>
          </b-row>
          <div v-if="booking.calculated" class="form-group row">
            <label class="col-4 col-form-label text-right">
              {{ $t('bookings.distance') }}
            </label>
            <div class="col-8 col-form-label">
              {{ booking.calculated.distance | distance }}
            </div>
          </div>
          <div v-if="booking.calculated" class="form-group row">
            <label class="col-4 col-form-label text-right">
              {{ $t('bookings.time') }}
            </label>
            <div class="col-8 col-form-label">
              {{ booking.calculated.time | humanizeDuration() }}
            </div>
          </div>
          <b-row v-if="booking.calculated" class="mb-4">
            <b-col cols="4" class="text-right">
              {{ $t('bookings.estimation') }}
            </b-col>
            <b-col cols="8">
              <booking-amount :booking="booking" :show-fees="false" />
            </b-col>
          </b-row>
        </b-card>

        <invoices
          :invoices="invoices"
          :show-url="true"
          :show-affiliate-commission="$account ? $account.affiliate : false"
          :show-partner-commission="$account ? $account.partner : false"
        />
        <cancel-reason
          v-if="model.status === 'cancelled'"
          :id="booking.cancel_reason_id"
          :text="booking.cancel_reason_text"
        />
        <payment-form-wrapper
          v-if="order"
          :order="order"
          :allowed-methods="allowedMethods"
          :cash-action-label="$t('orders.save_payment')"
          @methodChanged="onMethodChange"
        />

        <price-offers
          v-if="priceOffers.length"
          :offers="priceOffers"
          @accept="onPriceOfferAccept"
        />

        <b-card :title="$t('bookings.contact_info')">
          <b-row>
            <b-container>
              <p v-if="driver" class="d-flex items-center">
                <b class="pr-1">{{ $t('bookings.driver') }}:</b>
                {{ driver.phone }}
                {{ driver.name }}
              </p>
              <p
                v-if="booking.details && booking.details.vehicle_model"
                class="d-flex"
              >
                <b class="pr-1">{{ $t('bookings.vehicle_model') }}:</b>
                {{ booking.details.vehicle_model }}
              </p>
              <p
                v-if="booking.details && booking.details.vehicle_color"
                class="d-flex"
              >
                <b class="pr-1">{{ $t('bookings.vehicle_color') }}:</b>
                {{ booking.details.vehicle_color }}
              </p>
              <p
                v-if="booking.details && booking.details.vehicle_number"
                class="d-flex"
              >
                <b class="pr-1">{{ $t('bookings.vehicle_number') }}:</b>
                {{ booking.details.vehicle_number }}
              </p>
              <p v-if="company" class="d-flex items-center">
                <b class="pr-1">{{ $t('bookings.contact_local') }}:</b>
                <span>{{ company.phone }}</span>
              </p>
              <p
                v-if="$settings && $settings.contact_phone"
                class="d-flex items-center"
              >
                <b class="pr-1">{{ $t('bookings.contact_head') }}:</b>
                <span>{{ $settings.contact_phone }}</span>
              </p>
            </b-container>
            <b-col>
              <p
                v-if="booking.details && booking.details.company_notes"
                class="d-flex items-center"
              >
                <i class="icon-info22 text-info"></i>
                {{ booking.details.company_notes }}
              </p>
            </b-col>
          </b-row>
        </b-card>
        <div :class="review_allow" class="card">
          <span v-if="review_allow" class="disable_notify">
            {{ $t('bookings.avalable_reviews') }}
          </span>
          <div class="card-body">
            <h4 class="card-title">{{ $t('bookings.review_title') }}</h4>
            <div class="row review_driver flex flex-wrap">
              <div class="w-full review-rating  px-2 pt-2">
                <div v-if="review_exists" class="total_rating_wrap">
                  {{ $t('bookings.total_rating') }}:
                  <span>{{ total_rating }}</span>
                </div>

                <div>
                  <div class="wrap_star_rating">
                    <p>{{ $t('bookings.rating_star_punctual') }}</p>
                    <div>
                      <div class="star-rating">
                        <rate
                          :length="5"
                          :value="rating_punctual"
                          :disabled="!enabled"
                          name="rating_punctual"
                        ></rate>
                      </div>
                    </div>
                  </div>
                  <div class="wrap_star_rating">
                    <p>{{ $t('bookings.rating_star_comfort') }}</p>
                    <div>
                      <div class="star-rating">
                        <rate
                          :length="5"
                          :value="rating_comfort"
                          :disabled="!enabled"
                          name="rating_comfort"
                        ></rate>
                      </div>
                    </div>
                  </div>
                  <div class="wrap_star_rating">
                    <p>{{ $t('bookings.rating_star_clear') }}</p>
                    <div>
                      <div class="star-rating">
                        <rate
                          :length="5"
                          :value="rating_clean"
                          :disabled="!enabled"
                          name="rating_clean"
                        ></rate>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div class="w-full review-text px-2">
                <div class="form-group wrap_comment_tip">
                  <label for="comment" class="col-form-label text-sm-right">
                    {{ $t('bookings.rating_commit') }}
                  </label>
                  <textarea
                    id="comment"
                    v-model="comment"
                    :maxlength="maxlength"
                    :disabled="!enabled"
                    class="form-control"
                  />
                  <span class="comment_tip">
                    {{ $t('bookings.review_max') }}
                    {{ maxlength - comment.length }}
                    {{ $t('bookings.review_characters') }}
                  </span>
                </div>

                <div v-if="admin_reply" class="form-group">
                  <label class="col-form-label text-sm-right">{{
                    $t('bookings.review_admin_reply')
                  }}</label>
                  <b-textarea :value="admin_reply" readonly rows="4" />
                </div>

                <b-form-checkbox v-if="!enabled" v-model="enabled">
                  {{ $t('bookings.edit_review') }}
                </b-form-checkbox>

                <div v-if="enabled" class="">
                  <b-button type="button" variant="primary" @click="sendReview">
                    {{ $t('bookings.rating_submit') }}
                  </b-button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </b-col>
      <b-col>
        <b-card id="additional_info" :title="$t('bookings.additional_info')">
          <fields v-if="model.ride_type !== 'delivery'" ungrouped />

          <fields
            v-if="model.ride_type === 'delivery'"
            group="delivery_details"
          />
          <fields
            v-if="model.details && model.details.door_to_door"
            group="door_to_door_details"
          />
        </b-card>
        <b-card :title="$t('bookings.client_info')">
          <fields group="client" />
          <p>
            <i class="icon-exclamation text-info text-lg"></i>
            {{ $t('bookings.messages.client_updating') }}
          </p>
        </b-card>
      </b-col>
    </b-row>
    <b-card>
      <b-col class="actions flex justify-between">
        <div class="flex action-items">
          <b-button type="submit" variant="primary">
            {{ $t('general.save') }}
          </b-button>
          <b-button type="button" variant="light" class="ml-2" @click="cancel">
            {{ $t('general.back') }}
          </b-button>
        </div>

        <div class="flex action-items">
          <booking-cancel-btn
            v-if="canCancel"
            :button-text="cancelButtonText"
            :is-main="!booking.is_return"
            @click="cancelBooking"
          />
        </div>
      </b-col>
    </b-card>
    <ActionModal
      v-if="canCancel"
      id="cancel-modal"
      :loading="false"
      :title="$t('bookings.messages.confirm_cancelling')"
      :reasons="cancelReasons"
      @confirm="onConfirmCancel"
    />

    <b-modal
      ref="review_modal"
      v-model="showReviewServicesModal"
      :ok-title="$t('general.save')"
      centered
      ok-only
    >
      <p>{{ $t('reviews.services_modal_text') }}</p>
      <div
        v-for="service in reviewServices"
        :key="service.name"
        class="text-center"
      >
        <a :href="service.url">
          <img
            :alt="service.name"
            :src="service.image.url"
            :style="{
              width: service.image.width ? service.image.width + 'px' : '250px',
            }"
          />
        </a>
      </div>
    </b-modal>
  </form>
</template>

<script>
import BookingsApi from './Api'
import BookingForm from '../../../console/src/bookings/Form'
import PaymentFormWrapper from '../orders/PaymentFormWrapper'
import rate from '../components/Rating.vue'
import OrdersApi from '../orders/Api'

export default {
  name: 'BookingForm',

  components: {
    PaymentFormWrapper,
    rate,
  },

  extends: BookingForm,

  data() {
    return {
      review_allow: 'disable',
      rating_punctual: 0,
      rating_comfort: 0,
      rating_courtesy: 0,
      rating_clean: 0,
      comment: '',
      enabled: true,
      review_saved: false,
      maxlength: 1000,
      total_rating: 0,
      review_exists: false,
      review_id: 0,
      blocked: false,
      admin_reply: '',
      reviewServices: [],
      showReviewServicesModal: false,
    }
  },

  created() {
    this.$on('loaded', () => {
      if (['complete'].indexOf(this.model.status) !== -1) {
        this.review_allow = ''
      } else {
        this.review_allow = 'disable'
      }
      this.getReview()
    })
  },

  methods: {
    api() {
      return new BookingsApi({
        vehicle_id: this.model && this.model.vehicle_id,
      })
    },
    getReview() {
      let self = this
      self.disabled = true
      let promise = this.$http.get('reviews/booking?booking_id=' + this.id)
      self.$uiBlocker(promise, this.$refs.content)
      promise.then(result => {
        if (result.data === '') {
          self.enabled = true
        } else {
          self.total_rating = result.data.average
          self.rating_punctual = result.data.punctual
          self.rating_comfort = result.data.comfort
          self.rating_clean = result.data.clean
          self.comment = result.data.text
          self.review_id = result.data.id
          self.review_exists = true
          self.enabled = false
          self.admin_reply = result.data.admin_reply
        }
      })
    },
    sendReview() {
      let self = this
      let rating_punctual = document.querySelector(
        '.review_driver input[name=rating_punctual]',
      ).value
      let rating_comfort = document.querySelector(
        '.review_driver input[name=rating_comfort]',
      ).value
      let rating_clean = document.querySelector(
        '.review_driver input[name=rating_clean]',
      ).value

      let args = {
        text: this.comment,
        punctual: rating_punctual,
        comfort: rating_comfort,
        //courtesy: this.rating_courtesy,
        clean: rating_clean,
        booking_id: this.id,
      }
      if (this.review_exists) {
        args._method = 'PUT'
        let promise = this.$http.post('reviews/' + this.review_id, args)
        self.$uiBlocker(promise, this.$refs.content)
        promise.then(result => {
          if (result.data) {
            self.total_rating = result.data.average
            self.$toast.success('Review success updated!')
            self.review_exists = true
          }
        })
      } else {
        let promise = this.$http.post('reviews', args)
        self.$uiBlocker(promise, this.$refs.content)
        promise.then(result => {
          if (result.data) {
            self.total_rating = result.data.average
            self.$toast.success('Review success added!')
            self.review_exists = true
            self.reviewServices = result.data.review_services || []
            this.showReviewServicesModal = self.reviewServices.length > 0
          }
        })
      }
    },
    onMethodChange(method) {
      this.$uiBlocker(
        new OrdersApi().calculate(this.order, { method }).then(result => {
          this.order = result.data
        }),
      )
    },

    acceptPriceOfferRedirectRoute(result) {
      return {
        name: 'orders.pay',
        params: { id: result.order.id },
      }
    },
  },
}
</script>
<style scoped lang="scss">
@media (min-width: 1440px) {
  .review-text,
  .review-rating {
    max-width: 50%;
  }
}
#booking-info {
  #route {
    .col-sm-4 {
      max-width: 33.33333%;
      text-align: right !important;
    }
    .col-sm-8 {
      max-width: 66.66667%;
    }
  }
  .actions {
    button:not(:last-child) {
      margin-right: 0.5rem;
    }
  }
}
@media (max-width: 600px) {
  #booking-info {
    .actions {
      flex-direction: column;
      .action-items {
        flex-wrap: wrap;
        justify-content: center;
        button {
          width: 48%;
          margin: 1% !important;
        }
      }
    }
  }
}
</style>
